import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import RecoveryHashRedirect from './components/auth/RecoveryHashRedirect';

// Page components
import AuthPage from './pages/auth/AuthPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import QRComparePage from './pages/debug/QRComparePage';
import MainAnalyticsDashboard from './pages/main-analytics-dashboard';
import GeographicPerformance from './pages/geographic-performance';
import CampaignManagementHub from './pages/campaign-management-hub';
import QRCodeGenerator from './pages/qr-code-generator';
import QRCodeLibrary from './pages/qr-code-library';
import QRRedirect from './pages/qr-redirect';
import NotFound from './pages/NotFound';
import QRImportCenter from './pages/qr-import-center';

// Enhanced Protected Route Component with better error handling
const ProtectedRoute = ({ children }) => {
  const { user, loading, error } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Loading your dashboard...</p>
          <p className="text-sm text-gray-500">This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Show error state but allow development mode access
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Development Mode</h3>
                <p className="text-sm text-yellow-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <button
              onClick={() => window.location?.reload()}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry Connection
            </button>
            <button
              onClick={() => {
                // Continue to app anyway for development
                window.location.href = '/';
              }}
              className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
            >
              Continue in Development Mode
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Development Mode: Show preview with auth functionality
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <RecoveryHashRedirect />
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Auth Route */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/debug/qr-compare" element={<QRComparePage />} />
          
          {/* QR Redirect Routes - Public access required */}
          <Route path="/qr-redirect/:shortCode" element={<QRRedirect />} />
          <Route path="/qr/:shortCode" element={<QRRedirect />} />
          {/* Root short-link route - required for printed QR codes like https://qr.taylortravelmanagement.com/<shortCode> */}
          <Route path="/:shortCode" element={<QRRedirect />} />
          
          {/* Main Application Routes - QR Generator as Default */}
          <Route path="/" element={
            <ProtectedRoute>
              <QRCodeGenerator />
            </ProtectedRoute>
          } />
          
          <Route path="/qr-generator" element={
            <ProtectedRoute>
              <QRCodeGenerator />
            </ProtectedRoute>
          } />
          
          <Route path="/qr-import-center" element={
            <ProtectedRoute>
              <QRImportCenter />
            </ProtectedRoute>
          } />
          
          <Route path="/qr-library" element={
            <ProtectedRoute>
              <QRCodeLibrary />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <MainAnalyticsDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/geographic-performance" element={
            <ProtectedRoute>
              <GeographicPerformance />
            </ProtectedRoute>
          } />
          
          <Route path="/campaign-management" element={
            <ProtectedRoute>
              <CampaignManagementHub />
            </ProtectedRoute>
          } />

          {/* Legacy route redirects */}
          <Route path="/qr-code-generator" element={<Navigate to="/qr-generator" replace />} />
          <Route path="/qr-code-library" element={<Navigate to="/qr-library" replace />} />
          <Route path="/main-analytics-dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/campaign-management-hub" element={<Navigate to="/campaign-management" replace />} />

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;